import { useTranslation } from "react-i18next";
import type { IdeSession } from "~/api/opsTypes";
import { Pill } from "~/features/automation/components";
import {
  bindingChipState,
  inboundPolicyOf,
  isExtensionVersionBehind,
} from "~/features/console/ideSession";
import { Tooltip } from "~/foundation/components/Tooltip";

/** Liveness is the SERVER's word (`isOnline` from `lastSeenAt` vs a 90 s TTL), never derived here. */
export function OnlineBadge({ online }: { online: boolean }) {
  const { t } = useTranslation();
  return <Pill tone={online ? "success" : "neutral"}>{online ? t("Online") : t("Offline")}</Pill>;
}

/** The green/grey dot beside the title. Same source as the badge, so they can never disagree. */
export function OnlineDot({ online }: { online: boolean }) {
  const { t } = useTranslation();
  return (
    <span
      // `img` with a label: a coloured dot IS the information, so it needs a name a screen
      // reader can read — and a bare <span> may not carry `aria-label`.
      role="img"
      title={online ? t("Online") : t("Offline")}
      aria-label={online ? t("Online") : t("Offline")}
      className={`inline-block size-2 shrink-0 rounded-full ${
        online ? "bg-success" : "bg-text-tertiary"
      }`}
    />
  );
}

/**
 * What this window does with prompts sent from ANOTHER window.
 *
 * `allow` is the yellow one, and it is the whole reason this badge exists: it means work
 * arriving from elsewhere runs here **unattended**. `unknown` is a real answer — a session key
 * is minted per activation, so a window that has not been reloaded has genuinely not said —
 * and the card explains it rather than assuming a safe default that may not be true.
 */
export function PolicyBadge({ capabilities }: { capabilities: readonly string[] }) {
  const { t } = useTranslation();
  const policy = inboundPolicyOf(capabilities);
  const tone = policy === "allow" ? "warning" : policy === "unknown" ? "neutral" : "accent";
  const label =
    policy === "off"
      ? t("Inbound off")
      : policy === "ask"
        ? t("Inbound asks")
        : policy === "allow"
          ? t("Inbound allowed")
          : t("Inbound unknown");
  return (
    <Tooltip content={t("Inbound asks from other windows")}>
      <span>
        <Pill tone={tone}>{label}</Pill>
      </span>
    </Tooltip>
  );
}

/**
 * Which workspace this repo clone is bound to.
 *
 * A mismatch is the reason a clone's sync goes quiet with no error anywhere the person can
 * see: the server answers `409 binding_mismatch` to the extension, and the extension is the
 * only thing that hears it. A folder that syncs into its own workspace with its own token is
 * NOT that (`own-workspace`) and gets no warning. Only the first 8 characters of the foreign
 * id are shown — resolving it to a workspace NAME would leak across tenants.
 */
export function BindingChip({ session }: { session: IdeSession }) {
  const { t } = useTranslation();
  const state = bindingChipState(session);
  if (state === "unbound") {
    return <Pill tone="neutral">{t("Unbound")}</Pill>;
  }
  if (state === "bound") {
    return <Pill tone="neutral">{t("Bound · this workspace")}</Pill>;
  }
  if (state === "own-workspace") {
    return (
      <Pill tone="neutral">
        {t("Syncs to its own workspace ({{id}}…)", {
          id: (session.boundTeamId ?? "").slice(0, 8),
        })}
      </Pill>
    );
  }
  return (
    <Pill tone="warning">
      {t("Bound to another workspace ({{id}}…)", {
        id: (session.boundTeamId ?? "").slice(0, 8),
      })}
    </Pill>
  );
}

/** Advice, not an error: the window still works, it is just behind what the server suggests. */
export function VersionBadge({
  version,
  advised,
}: {
  version: string | null;
  advised: string | null;
}) {
  const { t } = useTranslation();
  if (!isExtensionVersionBehind(version, advised)) {
    return null;
  }
  return <Pill tone="warning">{t("Update advised (≥ {{version}})", { version: advised })}</Pill>;
}
